// src/components/EditableField.tsx
import React, { useState } from "react";
import {
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Box,
} from "@mui/material";

export interface EditableFieldProps {
    label: string;
    value: string;             // The current string value
    editMode: boolean;         // Whether to render in edit mode
    onChange?: (newValue: string) => void;
    type?: "text" | "select";
    options?: string[];
    placeholder?: string;
    validator?: (value: string) => string | null;
    readOnlyRenderer?: (value: string) => React.ReactNode;

    /**
     * If you want a custom editor component (e.g., Autocomplete),
     * provide a function that returns a React node. This will be
     * rendered instead of the default text/select field when editMode=true.
     */
    editor?: (value: string, onChange: (val: string) => void) => React.ReactNode;
}

const EditableField: React.FC<EditableFieldProps> = ({
                                                         label,
                                                         value,
                                                         editMode,
                                                         onChange,
                                                         type = "text",
                                                         options = [],
                                                         placeholder,
                                                         validator,
                                                         readOnlyRenderer,
                                                         editor,
                                                     }) => {
    const [error, setError] = useState<string | null>(null);

    const handleBlur = () => {
        if (validator) {
            const errorMsg = validator(value);
            setError(errorMsg);
        }
    };

    /**
     * 1) If not in edit mode, show label + readOnlyRenderer (or plain text).
     */
    if (!editMode) {
        return (
            <Box sx={{ marginBottom: "16px" }}>
                <Box sx={{ fontSize: "0.75rem", color: 'text.secondary', marginBottom: "4px" }}>
                    {label}
                </Box>
                <Box sx={{ fontSize: "1rem" }}>
                    {readOnlyRenderer ? readOnlyRenderer(value) : value}
                </Box>
            </Box>
        );
    }

    /**
     * 2) If a custom editor is provided, render it in edit mode.
     */
    if (editor && onChange) {
        return (
            <Box sx={{ marginBottom: "16px" }}>
                <Box sx={{ fontSize: "0.75rem", color: 'text.secondary', marginBottom: "4px" }}>
                    {label}
                </Box>
                {editor(value, onChange)}
            </Box>
        );
    }

    /**
     * 3) Otherwise, fall back to the default text/select logic in edit mode.
     */
    if (type === "select") {
        return (
            <FormControl fullWidth margin="normal" error={!!error}>
                <InputLabel>{label}</InputLabel>
                <Select
                    label={label}
                    value={value}
                    onChange={(e) => onChange?.(e.target.value as string)}
                    onBlur={handleBlur}
                >
                    {options.map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </Select>
                {error && <FormHelperText>{error}</FormHelperText>}
            </FormControl>
        );
    }

    // Default text field
    return (
        <TextField
            fullWidth
            margin="normal"
            label={label}
            value={value}
            placeholder={placeholder}
            onChange={(e) => onChange?.(e.target.value)}
            onBlur={handleBlur}
            error={!!error}
            helperText={error}
        />
    );
};

export default EditableField;
